import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";
import EventService from "service/event/EventService";
import ModalPortal from "../sections/common/ModalPortal";
import "../styles/uploadDocument.scss";
import PatientService from "../service/PatientService";
import {
  DOC_TYPE_OPTIONS,
  canonicalType,
  DESCRIPTION_MAX_CHARS,
  UPLOAD_ACCEPT_EXT,
  UPLOAD_MAX_BYTES,
  UPLOAD_MAX_LABEL,
  asciiFileName,
  fileExtension,
  formatFromFileName,
} from "../helper/documentConstants";
import useIsMobile from "mobile/useIsMobile";
import MOBILE from "mobile/mobileConfig";
import MobileFileSourceSheet from "mobile/components/MobileFileSourceSheet";

/**
 * "Upload document" — Figma 6326:6687 (empty) / 6728 (focused) / 6790
 * (uploading) / 6833 (ready) / 6876 (failed).
 *
 * WHERE THE BOARD AND THE API DISAGREE, and what this does about it. The board
 * draws the file as uploaded BEFORE Save ("Uploaded just now", a 62% progress
 * bar, Save enabled only afterwards), which implies a two-phase API: park the
 * file, then commit the record. There is no such API. document-service takes
 * the file AND the metadata in ONE multipart POST, so nothing can be uploaded
 * until Save is pressed. Two consequences, both deliberate:
 *
 *   · The chosen-file row says "Ready to upload", not "Uploaded just now" —
 *     the board's copy would be a claim about the server that is not yet true,
 *     and the operator would reasonably read it as "my file is safe now".
 *   · The progress state (6790) is used for the REAL upload, during Save. It
 *     shows no percentage, because it cannot: postWithFormData is built on
 *     fetch, which reports no upload progress at all. An animated
 *     indeterminate bar is the honest shape; a number here would be invented.
 *
 * Everything else follows the board exactly, including the failure row and its
 * Retry.
 */

/* Material Icons the board uses as font glyphs. phr loads no icon font, so each
   is inlined as its own path — the same thing DocumentsMenu does. */
const IconCloudUpload = () => (
  <svg className="up-drop-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
  </svg>
);

const IconPdf = () => (
  <svg className="up-file-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z" />
  </svg>
);

const IconImage = () => (
  <svg className="up-file-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);

const IconFolder = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
  </svg>
);
IconFolder.propTypes = { className: PropTypes.string };

const IconChevron = () => (
  <svg className="up-select-chev" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </svg>
);

const IconCheck = () => (
  <svg className="up-check-glyph" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

/** The board's close, exported verbatim (asset ed051bc0) — same as the popup's. */
const IconClose = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M7 7L17 17M17 7L7 17" />
  </svg>
);
IconClose.propTypes = { className: PropTypes.string };

/** "2.4 MB" — the board's own unit and precision. */
const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Why a file is refused, in the board's own words, or "" when it is fine.
 * Checked before anything is sent: the server answers a bare 500 "Not Allowed
 * Extension" for a bad type and has no size limit of its own, so neither
 * failure would otherwise reach the operator as a sentence they can act on.
 */
const rejectReason = (file) => {
  if (!file) return "";
  if (UPLOAD_ACCEPT_EXT.indexOf(fileExtension(file.name)) === -1) {
    return "Upload failed — only PDF, JPG or PNG files can be uploaded.";
  }
  if (file.size > UPLOAD_MAX_BYTES) {
    return `Upload failed — the file is larger than ${UPLOAD_MAX_LABEL}.`;
  }
  return "";
};

/**
 * EDIT MODE. Passing `editDocument` turns this into "Edit document": the same
 * three fields, no file. (Not named `document`: inside this component that would
 * shadow the DOM global the Escape handler uses.)
 *
 * One component rather than two because the fields ARE the form — Title,
 * Description and the folder picker with its per-folder counts — and a second
 * copy of them would drift, which is how the instruction map on the Rx screen
 * went stale. Only the file is upload-specific, and it is one conditional block.
 *
 * The folder is preselected through canonicalType: a legacy prescriptions
 * document carries type 4, which is deliberately absent from DOC_TYPE_OPTIONS,
 * so without the fold the picker would open on nothing for exactly those
 * documents. That is the read-path fold doing its job; what gets SENT is
 * whatever the operator has selected.
 */
const UploadDocumentModal = ({ patientId, groups, editDocument, onClose, onUploaded }) => {
  const editing = !!editDocument;
  const service = useRef(new PatientService()).current;
  const inputRef = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [title, setTitle] = useState(() => (editDocument && editDocument.title) || "");
  const [description, setDescription] = useState(
    /* Sliced on the way IN as well: a description stored before the cap can be
       longer than 250, and the counter would otherwise open reading "412 / 250"
       on a field the operator has not touched. */
    () => String((editDocument && editDocument.description) || "").slice(0, DESCRIPTION_MAX_CHARS)
  );
  const [type, setType] = useState(() =>
    editDocument && editDocument.type != null
      ? String(canonicalType(editDocument.type))
      : ""
  );
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  /* THE FOLDER MENU LEAVES THE MODAL. It was `position: absolute` inside
     `.up-body`, which is an `overflow-y: auto` scroller — so the menu was
     CLIPPED by the body's edge. Upload mode hid that (the picker sits high, with
     the drop zone below it); in edit mode the picker is the last field and the
     list was cut off behind the footer.
     usePopover portals it to <body> with fixed coordinates and flips it above
     the field when there is no room below, which is the case this bug was
     reported in. `DOC_TYPE_OPTIONS.length` is the reflow key — the menu's height
     is fixed content here, but passing it costs nothing and keeps the placement
     honest if the list ever changes. */
  const {
    triggerRef: typeTriggerRef,
    menuRef: typeMenuRef,
    menuStyle: typeMenuStyle,
  } = usePopover(typeOpen, () => setTypeOpen(false), DOC_TYPE_OPTIONS.length);
  /* One error slot for both halves of the failure state (6876): a file we
     refuse, and an upload the server refuses. They render identically and the
     operator's next move is the same. */
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  /* MOBILE ONLY: Browse files and Retry open "Upload from" (Camera / Photos /
     Files) instead of the input below. Off mobile both are false and idle. */
  const pickSource = useIsMobile() && MOBILE.uploadSourceSheet;
  const [sourceOpen, setSourceOpen] = useState(false);

  const takeFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setError(rejectReason(f));
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  /* "4 documents" / "1 document" / "No documents", off the SAME normalizeDocGroups
     the folder grid uses — a second count derived another way would drift. */
  const countFor = (value) => {
    const g = (groups || []).find((x) => x && canonicalType(x.type) === value);
    const n = (g && g.all && g.all.length) || 0;
    if (!n) return "No documents";
    return `${n} document${n === 1 ? "" : "s"}`;
  };
  const chosen = DOC_TYPE_OPTIONS.find((o) => String(o.value) === String(type));

  /* EDIT needs no file, but it does need a CHANGE — an enabled Save on an
     untouched form invites a write that can only blank something, since the
     rename handler assigns both fields unconditionally. */
  const editDirty =
    editing &&
    (title.trim() !== String(editDocument.title || "").trim() ||
      description.trim() !== String(editDocument.description || "").trim() ||
      String(type) !== String(canonicalType(editDocument.type)));
  const ready = editing
    ? !!(title.trim() && type !== "" && editDirty && !saving)
    : !!(title.trim() && type !== "" && file && !error && !saving);

  /**
   * TWO ENDPOINTS, and only the halves that changed.
   *
   * document-service splits them: PATCH /{id}/name takes { title, description }
   * and PATCH /{id}/type takes { type }. Both are sent when both changed, name
   * first — if the second fails the operator is told, and the folder is the half
   * still showing the old value, which is the one they can see and retry.
   *
   * Nothing is patched locally afterwards: a folder change moves the row out of
   * the folder on screen and changes two folder counts, so the panel refetches
   * through the same callback an upload uses rather than trying to reconcile
   * that by hand.
   */
  const saveEdit = useCallback(() => {
    if (!ready) return;
    setSaving(true);
    setError("");
    const done = () => {
      if (!mounted.current) return;
      setSaving(false);
      if (onUploaded) onUploaded();
      if (onClose) onClose();
    };
    const fail = (err) => {
      if (!mounted.current) return;
      setSaving(false);
      setError(
        (err && err.message) ||
          "The changes could not be saved. Please try again."
      );
    };
    const typeChanged = String(type) !== String(canonicalType(editDocument.type));
    const nameChanged =
      title.trim() !== String(editDocument.title || "").trim() ||
      description.trim() !== String(editDocument.description || "").trim();
    const moveFolder = () =>
      typeChanged
        ? service.updateDocumentType(editDocument.id, Number(type), done, fail)
        : done();
    if (nameChanged) {
      service.updateDocumentNameAndDescription(
        editDocument.id,
        /* Both fields every time — the handler assigns each unconditionally, so
           a partial body blanks the other one. */
        { title: title.trim(), description: description.trim() },
        moveFolder,
        fail
      );
      return;
    }
    moveFolder();
  }, [ready, service, editDocument, title, description, type, onUploaded, onClose]);

  const save = useCallback(() => {
    if (!ready) return;
    if (editing) {
      saveEdit();
      return;
    }
    setSaving(true);
    setError("");
    service.uploadPatientDocument(
      patientId,
      file,
      {
        /* The title is the operator's, NOT the file name — the whole reason
           this does not reuse AppointmentService.uploadDocument. */
        title: title.trim(),
        description: description.trim(),
        /* ASCII-safe: a multibyte character in the name panics the server's
           extension parser into a 500. The title keeps whatever was typed. */
        fileName: asciiFileName(file.name),
        type: Number(type),
        format: formatFromFileName(file.name),
        /* 4 = the status every other document carries; bridge's own upload
           sends the model's default and its model is seeded with 4. */
        status: 4,
      },
      () => {
        if (!mounted.current) return;
        setSaving(false);
        /* The upload used to succeed in silence: the modal closed and the list
           refreshed, and nothing SAID it had worked (2026-10-05). The app's
           success toast — "showError" with tone "success" is what phr's
           ToastHost draws green; bridge's GlobalError shows the same message in
           its own snackbar, as for every other confirmation synced from phr. */
        EventService.emit("showError", { message: "Document uploaded", tone: "success" });
        if (onUploaded) onUploaded();
        if (onClose) onClose();
      },
      (err) => {
        if (!mounted.current) return;
        setSaving(false);
        setError(
          (err && err.message) ||
            "Upload failed — the document could not be saved. Please try again."
        );
      }
    );
  }, [ready, editing, saveEdit, service, patientId, file, title, description, type, onUploaded, onClose]);

  /* Escape closes, but never mid-write — a half-finished upload that vanishes
     is worse than one the operator has to wait out. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (saving) return;
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    takeFile(f);
  };

  const isImage = file && /^(jpg|jpeg|png|gif)$/.test(fileExtension(file.name));

  return (
    <ModalPortal>
      <div
        className="up-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving && onClose) onClose();
        }}
      >
        <div
          className="up-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Upload document"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="up-head">
            <span className="up-title">{editing ? "Edit document" : "Upload document"}</span>
            <button
              type="button"
              className="up-close"
              onClick={() => !saving && onClose && onClose()}
              disabled={saving}
              aria-label="Close"
            >
              <IconClose className="up-close-icon" />
            </button>
          </div>

          <div className="up-body">
            <label className="up-field">
              <span className="up-label">
                Title <i className="up-req">*</i>
              </span>
              <input
                type="text"
                className="up-input"
                placeholder="e.g. Lipid profile — 13 May 2026"
                value={title}
                maxLength={160}
                disabled={saving}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <label className="up-field">
              <span className="up-label">
                Description <i className="up-optional">Optional</i>
                {/* The tally once anything is typed, the ceiling while empty —
                    the same shape the v2 section forms use for their capped
                    descriptions. maxLength alone just stops accepting keystrokes,
                    which on a field whose placeholder invites a sentence reads as
                    the box having broken. */}
                <i className="up-count">
                  {description
                    ? `${description.length} / ${DESCRIPTION_MAX_CHARS}`
                    : `Max ${DESCRIPTION_MAX_CHARS} characters`}
                </i>
              </span>
              <textarea
                className="up-textarea"
                placeholder="Add a short note — what this document covers, who ordered it."
                value={description}
                maxLength={DESCRIPTION_MAX_CHARS}
                disabled={saving}
                /* CAPPED IN THE HANDLER TOO, not just by the attribute.
                   maxLength binds real keystrokes and pastes and nothing else,
                   so any value arriving another way sailed past it and the
                   counter then read "300 / 250" — the same lesson the number
                   inputs on this screen already carry, where maxLength is inert
                   and the value has to be clamped before it is stored. */
                onChange={(e) =>
                  setDescription(String(e.target.value).slice(0, DESCRIPTION_MAX_CHARS))
                }
              />
            </label>

            {/* a <div>, not a <label>: the control is a button now, and a label
                wrapping it would re-toggle the menu on every click. */}
            <div className="up-field">
              <span className="up-label">
                Type of document <i className="up-req">*</i>
              </span>
              {/* NOT a native <select>: the board draws each folder with its own
                  glyph, its document count and a tick on the chosen one
                  (6326:6767), none of which an <option> can carry. */}
              <span className="up-select-wrap">
                <button
                  type="button"
                  ref={typeTriggerRef}
                  className={`up-select${type === "" ? " is-empty" : ""}`}
                  disabled={saving}
                  aria-haspopup="listbox"
                  aria-expanded={typeOpen}
                  onClick={() => setTypeOpen((v) => !v)}
                >
                  {chosen ? chosen.label : "Select a folder"}
                </button>
                <IconChevron />

                {typeOpen && (
                  <ModalPortal>
                  <span
                    className="up-menu"
                    role="listbox"
                    ref={typeMenuRef}
                    style={typeMenuStyle}
                  >
                    {DOC_TYPE_OPTIONS.map((o, i) => {
                      const on = String(o.value) === String(type);
                      return (
                        <React.Fragment key={o.value}>
                          {i > 0 && <span className="up-menu-div" aria-hidden="true" />}
                          <button
                            type="button"
                            role="option"
                            aria-selected={on}
                            className={`up-opt${on ? " is-on" : ""}`}
                            onClick={() => {
                              setType(String(o.value));
                              setTypeOpen(false);
                            }}
                          >
                            <IconFolder className="up-opt-folder" />
                            <span className="up-opt-name">{o.label}</span>
                            <span className="up-opt-count">{countFor(o.value)}</span>
                            {on && <IconCheck />}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </span>
                  </ModalPortal>
                )}
              </span>
            </div>

            {/* The only upload-specific field. An edit never replaces the
                file — document-service has no route for it, and the two PATCHes
                carry metadata alone. */}
            {!editing && (
            <div className="up-field">
              <span className="up-label">
                Upload <i className="up-req">*</i>
              </span>

              <input
                ref={inputRef}
                type="file"
                className="up-file-input"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => takeFile(e.target.files && e.target.files[0])}
              />

              {!file ? (
                <div
                  className={`up-drop${dragging ? " is-dragging" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                >
                  <span className="up-drop-badge" aria-hidden="true">
                    <IconCloudUpload />
                  </span>
                  <span className="up-drop-head">Drag and drop your file here</span>
                  <span className="up-drop-sub">PDF, JPG or PNG · up to {UPLOAD_MAX_LABEL}</span>
                  <button
                    type="button"
                    className="up-browse"
                    onClick={
                      pickSource
                        ? () => setSourceOpen(true)
                        : () => inputRef.current && inputRef.current.click()
                    }
                  >
                    Browse files
                  </button>
                </div>
              ) : (
                <div className={`up-file${error ? " is-error" : ""}`}>
                  <span className={`up-file-tile${isImage ? " is-img" : ""}`} aria-hidden="true">
                    {isImage ? <IconImage /> : <IconPdf />}
                  </span>
                  <span className="up-file-meta">
                    <span className="up-file-name">{file.name}</span>
                    {saving && (
                      <span className="up-track" aria-hidden="true">
                        <span className="up-fill" />
                      </span>
                    )}
                    {error ? (
                      <span className="up-file-error">{error}</span>
                    ) : (
                      <span className="up-file-sub">
                        {saving
                          ? `Uploading… ${formatBytes(file.size)}`
                          : `${(formatFromFileName(file.name) === 4 ? "PDF" : fileExtension(file.name).toUpperCase())}  ·  ${formatBytes(file.size)}  ·  Ready to upload`}
                      </span>
                    )}
                  </span>

                  {error && !saving && (
                    <button
                      type="button"
                      className="up-retry"
                      onClick={
                        pickSource
                          ? () => setSourceOpen(true)
                          : () => inputRef.current && inputRef.current.click()
                      }
                    >
                      Retry
                    </button>
                  )}
                  {!error && !saving && (
                    <span className="up-check" aria-hidden="true">
                      <IconCheck />
                    </span>
                  )}
                  <button
                    type="button"
                    className="up-file-remove"
                    onClick={clearFile}
                    disabled={saving}
                    aria-label="Remove file"
                  >
                    <IconClose className="up-file-remove-icon" />
                  </button>
                </div>
              )}

              {pickSource && (
                <MobileFileSourceSheet
                  open={sourceOpen}
                  onClose={() => setSourceOpen(false)}
                  onFile={takeFile}
                />
              )}
            </div>
            )}

            {/* An edit's failure has no file row to live in, so it says so
                here instead of vanishing. */}
            {editing && error && <p className="up-edit-error">{error}</p>}
          </div>

          <div className="up-foot">
            <button
              type="button"
              className="up-btn up-btn-ghost"
              onClick={() => !saving && onClose && onClose()}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="up-btn up-btn-primary"
              onClick={save}
              disabled={!ready}
            >
              {saving ? "Saving…" : editing ? "Save changes" : "Save document"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

UploadDocumentModal.propTypes = {
  patientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /** The popup's own folder groups, for the per-folder counts. */
  groups: PropTypes.array,
  /** Passing one switches to EDIT: same fields, prefilled, no file.
      NOT named `document` — that would shadow the DOM global inside this
      component, where the Escape handler needs it. */
  editDocument: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.number,
  }),
  onClose: PropTypes.func,
  /** Called after a successful save, so the folder grid can refetch. */
  onUploaded: PropTypes.func,
};

export default UploadDocumentModal;
